import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardService } from '../services/dashboard';

interface DashboardStats {
  revenueToday: number;
  revenue7d: number;
  revenue30d: number;
  activeTickets: number;
  totalParkings: number;
  totalUsers: number;
  totalTickets: number;
  totalRevenue: number;
  ticketsToday: number;
  newUsersToday: number;
  avgTicketDuration: number;
}

interface RecentTicket {
  id: string;
  plate: string;
  parking: string;
  status: string;
  amount: number;
  time: string;
  userPhone: string;
  createdAt: string;
}

interface Alert {
  type: 'warning' | 'error' | 'success';
  title: string;
  message: string;
  icon: string;
}

interface TopParking {
  name: string;
  revenue: number;
  tickets: number;
}

interface DashboardState {
  stats: DashboardStats | null;
  recentTickets: RecentTicket[];
  alerts: Alert[];
  topParkings: TopParking[];
  isLoading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  stats: null,
  recentTickets: [],
  alerts: [],
  topParkings: [],
  isLoading: false,
  error: null,
};

export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async () => {
    const response = await dashboardService.getStats();
    return (response as any).data;
  }
);

export const fetchRecentTickets = createAsyncThunk(
  'dashboard/fetchRecentTickets',
  async (limit: number = 10) => {
    const response = await dashboardService.getRecentTickets(limit);
    return (response as any).data;
  }
);

export const fetchAlerts = createAsyncThunk(
  'dashboard/fetchAlerts',
  async () => {
    const response = await dashboardService.getAlerts();
    return (response as any).data;
  }
);

export const fetchTopParkings = createAsyncThunk(
  'dashboard/fetchTopParkings',
  async (limit: number = 5) => {
    const response = await dashboardService.getTopParkings(limit);
    return (response as any).data;
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erreur lors du chargement';
      })
      .addCase(fetchRecentTickets.fulfilled, (state, action) => {
        state.recentTickets = action.payload;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.alerts = action.payload;
      })
      .addCase(fetchTopParkings.fulfilled, (state, action) => {
        state.topParkings = action.payload;
      });
  },
});

export default dashboardSlice.reducer;
